import React, { useState, useRef } from 'react'
import { TextInput } from 'react-admin'
import { 
  InputAdornment, 
  IconButton, 
  Tooltip,
  Box
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { History as HistoryIcon } from '@mui/icons-material'
import { useController } from 'react-hook-form'
import { useTranslate, useRecordContext } from 'ra-core'
import CoordinatePicker from 'components//ui/mapview/coord.picker'
import FieldAuditModal from './field.audit.modal'

interface CoordinateInputWithAuditProps {
  source: string
  label?: string
  disabled?: boolean
  validate?: any[]
  tableName: string
  showAuditIcon?: boolean
  position?: 'end' | 'start'
  [key: string]: any
}

const CoordinateInputWithAudit: React.FC<CoordinateInputWithAuditProps> = ({
  source,
  label,
  disabled = false,
  validate,
  tableName,
  showAuditIcon = true,
  position = 'end',
  ...props
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [auditModalOpen, setAuditModalOpen] = useState(false)
  const { field } = useController({ name: source })
  const translate = useTranslate()
  const record = useRecordContext()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleCoordinateSelect = (coordinates: string) => {
    field.onChange(coordinates)
  }

  const handleMapIconClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setPickerOpen(true)
  }

  const handleAuditIconClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setAuditModalOpen(true)
  }

  // Создаем массив иконок в зависимости от настроек
  const adornments = []
  
  // Добавляем иконку карты
  adornments.push(
    <Tooltip key="map" title={translate('internal.coordinate_picker.select_on_map')}>
      <span>
        <IconButton
          onClick={handleMapIconClick}
          disabled={disabled}
          size="small"
          tabIndex={-1}
        >
          <LocationOnIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  )

  // Добавляем иконку аудита, если нужно
  if (showAuditIcon) {
    adornments.push(
      <Tooltip key="audit" title={translate('internal.audit.show_history')}>
        <span>
          <IconButton
            onClick={handleAuditIconClick}
            size="small"
            tabIndex={-1}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  // Если нет иконок, используем обычный TextInput
  if (adornments.length === 0) {
    return <TextInput source={source} label={label} disabled={disabled} validate={validate} {...props} />
  }

  // Создаем InputProps с иконками
  const inputProps = {
    ...props.InputProps,
    [position === 'end' ? 'endAdornment' : 'startAdornment']: (
      <InputAdornment position={position}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {adornments}
        </Box>
      </InputAdornment>
    )
  }

  const recordId = record ? String((record as any).id || '') : ''
  const currentValue = field.value

  return (
    <Box>
      <TextInput
        ref={inputRef}
        source={source}
        label={label}
        disabled={disabled}
        validate={validate}
        InputProps={inputProps}
        {...props}
      />
      <CoordinatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleCoordinateSelect}
        initialCoordinates={field.value || ''}
      />
      {showAuditIcon && (
        <FieldAuditModal
          isOpen={auditModalOpen}
          onClose={() => setAuditModalOpen(false)}
          tableName={tableName}
          recordId={recordId}
          fieldName={source}
          fieldLabel={label || source}
          currentValue={currentValue}
        />
      )}
    </Box>
  )
}

export default CoordinateInputWithAudit
